import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import ProductCard from '../components/ProductCard';
import { FiSearch, FiX, FiTrendingUp, FiClock } from 'react-icons/fi';
import { AuroraBlobs, GrainOverlay, GlassChip, Reveal, fadeUp, stagger, MagneticButton } from '../components/ui';

const TRENDING = ['Anarkali wedding', 'Cotton kurti M', 'Office wear', 'Block print indigo', 'VritiEthnic festive'];
const RECENT_KEY = 'vritiethnic_recent_searches';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [recent, setRecent] = useState(() => JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'));
  const navigate = useNavigate();

  useEffect(() => {
    const param = searchParams.get('q');
    if (param && param !== q) setQ(param);
  }, [searchParams]);

  const { data, isFetching } = useQuery({
    queryKey: ['search', q],
    queryFn: () => api.get(`/products?search=${encodeURIComponent(q)}&limit=12`),
    enabled: q.length >= 2,
  });

  const handleSearch = (term) => {
    const updated = [term, ...recent.filter(r => r !== term)].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
    setRecent(updated);
    setQ(term);
    setSearchParams({ q: term });
  };

  const removeRecent = (term, e) => {
    e.stopPropagation();
    const updated = recent.filter(r => r !== term);
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
    setRecent(updated);
  };

  const products = data?.products || [];

  return (
    <div>
      {/* Hero with search */}
      <section style={{ position: 'relative', background: '#1A100E', overflow: 'hidden', padding: '52px 24px 44px' }}>
        <AuroraBlobs muted />
        <GrainOverlay />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 800, margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <GlassChip>Search</GlassChip>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="serif" style={{ fontSize: 'clamp(32px, 4vw, 46px)', color: '#FFF', lineHeight: 1.05, letterSpacing: '-0.02em', marginTop: 16, marginBottom: 22 }}>
            What are you looking for?
          </motion.h1>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(237,227,209,0.18)',
            borderRadius: 999, padding: '6px 8px 6px 22px',
            backdropFilter: 'blur(20px) saturate(160%)', WebkitBackdropFilter: 'blur(20px) saturate(160%)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
          }}>
            <FiSearch size={18} style={{ color: 'rgba(237,227,209,0.6)' }} />
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && q && handleSearch(q)}
              placeholder="Search kurtis, anarkali, sets…"
              style={{ flex: 1, height: 52, border: 'none', outline: 'none', fontSize: 16, background: 'transparent', color: '#FFF', fontFamily: 'inherit' }}
            />
            {q && (
              <button onClick={() => { setQ(''); setSearchParams({}); }} style={{ width: 32, height: 32, border: 'none', background: 'rgba(255,255,255,0.06)', borderRadius: '50%', color: 'rgba(237,227,209,0.6)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiX size={14} />
              </button>
            )}
          </motion.div>
        </div>
      </section>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '36px 24px 64px' }}>
        <AnimatePresence mode="wait">
          {q.length < 2 ? (
            <motion.div key="initial" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {/* Trending */}
              <Reveal>
                <div style={{ marginBottom: 36 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <FiTrendingUp size={14} color="var(--gold)" />
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--gold)' }}>Trending</div>
                  </div>
                  <motion.div variants={stagger} initial="hidden" animate="show" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {TRENDING.map(t => (
                      <motion.button key={t} variants={fadeUp} onClick={() => handleSearch(t)} whileHover={{ y: -2 }} whileTap={{ scale: 0.96 }}
                        className="chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(12px)' }}>
                        🔥 {t}
                      </motion.button>
                    ))}
                  </motion.div>
                </div>
              </Reveal>

              {/* Recent */}
              {recent.length > 0 && (
                <Reveal>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                      <FiClock size={14} color="var(--muted)" />
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--muted)' }}>Recent</div>
                    </div>
                    <motion.div variants={stagger} initial="hidden" animate="show" style={{ display: 'flex', flexDirection: 'column' }}>
                      {recent.map(r => (
                        <motion.div key={r} variants={fadeUp} onClick={() => handleSearch(r)} whileHover={{ x: 4 }}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 4px', borderBottom: '1px solid var(--line-2)', cursor: 'pointer' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--ink-2)', fontSize: 14 }}>
                            <FiSearch size={14} color="var(--muted)" /> {r}
                          </div>
                          <button onClick={e => removeRecent(r, e)} style={{ border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', padding: 6 }}>
                            <FiX size={14} />
                          </button>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>
                </Reveal>
              )}
            </motion.div>
          ) : (
            <motion.div key="results" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
              {isFetching ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)', fontSize: 13 }}>Searching…</div>
              ) : products.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <h2 className="serif" style={{ fontSize: 26, marginBottom: 10 }}>No results for "{q}"</h2>
                  <div style={{ fontSize: 13, color: 'var(--muted)' }}>Try a different search term</div>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 22, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{data?.total} results for "{q}"</div>
                  <motion.div variants={stagger} initial="hidden" animate="show" className="search-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 22 }}>
                    {products.map(p => (
                      <motion.div key={p._id} variants={fadeUp} whileHover={{ y: -6 }}>
                        <ProductCard product={p} />
                      </motion.div>
                    ))}
                  </motion.div>
                  {data?.total > 12 && (
                    <div style={{ textAlign: 'center', marginTop: 32 }}>
                      <MagneticButton variant="outline" onClick={() => navigate(`/category/all?search=${encodeURIComponent(q)}`)}>View all {data.total} results</MagneticButton>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <style>{`
        @media (max-width: 900px) { .search-grid { grid-template-columns: repeat(2, 1fr) !important; } }
      `}</style>
    </div>
  );
}
